/**
 * Shared demo framework — global demo-mode flag.
 *
 * A single on/off switch for ALL canned demos at once. When on, every demo
 * serves fixtures (AP, AR, Workflows, Integrations); when off, the real app
 * renders. Toggled from the hidden Cmd+K command (see `@/demo/command`) and
 * persisted per-tab in sessionStorage so navigation keeps it on; a reload
 * re-reads it.
 *
 * Read synchronously from sessionStorage (not React state) so hook-shims and the
 * oRPC client link can branch on it without threading a context everywhere. The
 * value is captured once at module load and after any explicit toggle.
 *
 * `isDemoActive` / `getActiveDemo` / `setActiveDemo` are kept as thin
 * compatibility shims over the global flag so existing call sites (the per-demo
 * `is*DemoMode()` wrappers, the hook-shim factory, the email viewer) keep
 * working without change.
 */

const SESSION_KEY = 'demo-mode';
const ON_VALUE = 'on';

function readFromEnvironment(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return window.sessionStorage.getItem(SESSION_KEY) === ON_VALUE;
  } catch {
    return false;
  }
}

let demoMode = readFromEnvironment();

/** True when demo mode is on (i.e. every demo is active). */
export function isDemoMode(): boolean {
  return demoMode;
}

/** Turn demo mode on or off (affects every demo at once). */
export function setDemoMode(on: boolean): void {
  try {
    if (on) {
      window.sessionStorage.setItem(SESSION_KEY, ON_VALUE);
    } else {
      window.sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    // sessionStorage unavailable — fall back to in-memory only.
  }
  demoMode = on;
}

/** Re-read the flag from sessionStorage (e.g. after navigation). */
export function refreshDemoMode(): boolean {
  demoMode = readFromEnvironment();
  return demoMode;
}

// --- Compatibility shims over the single global flag -----------------------
// All demos now share one switch; the per-key API is retained so existing call
// sites keep working (the key is ignored — every demo is active together).

/** @deprecated prefer `isDemoMode()`. True when demo mode is on (key ignored). */
export function isDemoActive(_key?: string): boolean {
  return demoMode;
}

/** @deprecated prefer `isDemoMode()`. A truthy token when on, else `null`. */
export function getActiveDemo(): string | null {
  return demoMode ? ON_VALUE : null;
}

/** @deprecated prefer `setDemoMode()`. Any non-null key turns demo mode on. */
export function setActiveDemo(key: string | null): void {
  setDemoMode(key !== null);
}
